import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, RefreshCw, Users, FolderOpenDot, GraduationCap, Clock,
  Mail, ExternalLink, FileDown, CheckCircle2, CalendarClock, ArrowRight, Heart,
} from "lucide-react";
import { COLORS } from "@/lib/api";

const REPORTES_URL = "https://bit.ly/ReportesClubJL";
const REGISTRO_URL = "https://forms.office.com/r/shDQF8wzkS";
const OFICINA_URL = "https://calendly.com/cristina-latentes/30min";
const PLAN_DOC = "/docs/plan-de-trabajo-nueva-mesa.docx";
const CRISTINA = "cristina@latentes.mx";

const fade = { hidden: { opacity: 0, y: 18 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };

const TIPS = [
  {
    icon: Clock,
    t: "Tip #1: Anticípate",
    c: COLORS.magenta,
    body: [
      "Si llevas más de un año como líder de Club, es momento de transicionar. Si es un Club nuevo, la gestión puede durar hasta dos años.",
      "Ponte en contacto con la equipa de Clubs y, una vez que tengas a tu(s) nueva(s) líder(es), asegúrate de que llenen el Reporte de Inicio.",
    ],
    bullets: ["Facilita la promoción de la convocatoria", "Ayuda a hacer buenas entrevistas", "Hace la transición más natural y transparente"],
  },
  {
    icon: FolderOpenDot,
    t: "Tip #2: Documenta",
    c: COLORS.orange,
    body: [
      "Ten siempre una carpeta (virtual o física) con la información esencial del Club, no solo en el cambio de gestión.",
    ],
    bullets: ["Contraseñas (correo, redes y herramientas — acceso privado solo para líderes)", "Organigrama", "Estatutos del Club", "Historia, misión, visión y objetivos", "Eventos, tradiciones y aprendizajes"],
  },
  {
    icon: GraduationCap,
    t: "Tip #3: Capacita",
    c: COLORS.lime,
    body: [
      "Reúnanse para que todos los roles compartan cómo fue la dinámica de su área, recomendaciones y consejos.",
      "¡Siempre es bueno refrescar la memoria! Las capacitaciones son necesarias aunque la nueva mesa ya haya formado parte de la junta anterior.",
    ],
    bullets: [],
  },
];

const PROCESO = [
  {
    num: "1",
    icon: Users,
    title: "La mesa saliente envía su tabla de equipo",
    content: (
      <>
        <p>
          Escribe un correo a{" "}
          <a href={`mailto:${CRISTINA}`} className="font-semibold text-brand-magenta hover:underline">{CRISTINA}</a>{" "}
          con una tabla del equipo saliente que incluya:
        </p>
        <ul className="space-y-1.5 text-sm">
          <li>• <span className="font-semibold text-brand-ink">Nombre</span> de cada integrante</li>
          <li>• <span className="font-semibold text-brand-ink">Puesto</span> que desempeñó</li>
          <li>• <span className="font-semibold text-brand-ink">Meses activos</span> (mes a mes) en los que participó</li>
        </ul>
        <p className="text-sm text-brand-ink/70">Cristina les hará llegar sus reconocimientos en ese mismo correo. 💛</p>
      </>
    ),
  },
  {
    num: "2",
    icon: RefreshCw,
    title: "Presenta a la nueva mesa y confirma reportes",
    content: (
      <>
        <p>En el correo comparte el <span className="font-semibold text-brand-ink">contacto (correo y teléfono)</span> de la(s) nueva(s) líder(es) y confirma que:</p>
        <ul className="space-y-2.5">
          <li className="flex items-start gap-2">
            <CheckCircle2 size={18} className="text-brand-magenta flex-shrink-0 mt-0.5" />
            <span>La nueva líder ya llenó su{" "}
              <a href={REGISTRO_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-magenta hover:underline inline-flex items-center gap-1">Reporte de Inicio <ExternalLink size={13} /></a>.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={18} className="text-brand-magenta flex-shrink-0 mt-0.5" />
            <span>La mesa saliente completó sus{" "}
              <a href={REPORTES_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-magenta hover:underline inline-flex items-center gap-1">Reportes de Actividades <ExternalLink size={13} /></a>.
            </span>
          </li>
        </ul>
      </>
    ),
  },
  {
    num: "3",
    icon: FileDown,
    title: "La nueva mesa llena el Plan de Trabajo",
    content: (
      <>
        <p>La nueva mesa debe llenar el documento <span className="font-semibold text-brand-ink">Plan de Trabajo</span> y enviarlo por correo a{" "}
          <a href={`mailto:${CRISTINA}`} className="font-semibold text-brand-magenta hover:underline">{CRISTINA}</a>.
        </p>
        <a href={PLAN_DOC} download data-testid="plan-download" className="inline-flex items-center gap-2 bg-brand-ink text-white font-semibold px-5 py-3 rounded-sm hover:bg-brand-orange transition-colors">
          Descargar Plan de Trabajo <FileDown size={16} />
        </a>
      </>
    ),
  },
  {
    num: "4",
    icon: CalendarClock,
    title: "Agenda tu reunión de equipo",
    content: (
      <>
        <p>Agenda una reunión de equipo con la Líder de Clubs para acompañar a la nueva mesa.</p>
        <a href={OFICINA_URL} target="_blank" rel="noopener noreferrer" data-testid="oficina-link" className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink font-semibold px-5 py-3 rounded-sm hover:bg-brand-ink hover:text-white transition-colors">
          Agendar en Oficina Virtual <ExternalLink size={16} />
        </a>
      </>
    ),
  },
];

const Step = ({ num, icon: Icon, title, children, last }) => (
  <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="flex gap-5">
    <div className="flex flex-col items-center">
      <span className="w-11 h-11 rounded-sm flex items-center justify-center bg-brand-ink text-white font-heading font-bold flex-shrink-0">{num}</span>
      {!last && <span className="flex-1 w-px bg-[#E5E5E5] my-2" />}
    </div>
    <div className={last ? "" : "pb-10"}>
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-brand-magenta" />
        <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">{title}</h3>
      </div>
      <div className="mt-3 text-brand-ink/80 leading-relaxed space-y-3">{children}</div>
    </div>
  </motion.div>
);

const TransicionLiderazgos = () => {
  return (
    <div data-testid="subpage-transicion-de-liderazgos">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0">
          <img src="/club-banner.png" alt="Transición de liderazgos" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,17,0.45), rgba(17,17,17,0.82))" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link to="/red-de-clubs" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Red de Clubs
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.yellow }} />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Transición de Liderazgos
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 font-medium max-w-2xl">
            ¡Se reinicia la cuenta! Cede el Club a nuevas integrantes que continúen su legado,
            formando una nueva Junta de Liderazgos.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Qué es */}
        <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">¿Qué es una transición de liderazgos?</h2>
          <p className="mt-4 text-brand-ink/80 leading-relaxed">
            Es ceder el grupo a nuevas integrantes que continúen con su legado y la trascendencia del Club,
            formando una nueva Junta de Liderazgos. Recuerda que en Juventudes Latentes manejamos co-liderazgos.
          </p>
          <div className="mt-5 border-l-4 pl-4 py-1" style={{ borderColor: COLORS.yellow }}>
            <p className="font-semibold text-brand-ink">Si llevas más de un año como líder de Club… ¡es momento de transicionar!</p>
          </div>
        </motion.section>

        {/* Cómo escoger nueva junta */}
        <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="mt-14">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">¿Cómo escoger una nueva Junta de Liderazgo?</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { t: "Abre convocatoria", d: "Difunde entre tu comunidad estudiantil o localidad." },
              { t: "Analiza perfiles", d: "Realiza entrevistas a las candidatas interesadas." },
              { t: "Selecciona perfiles", d: "Si ya hay interesadas, reúnanse y busquen un acuerdo; si no, hagan una votación transparente." },
              { t: "Invita a participar", d: "¡Tu Club ya tiene a su(s) próxima(s) líder(es)!" },
            ].map((s, i) => (
              <div key={s.t} className="border border-[#E5E5E5] rounded-sm p-4">
                <span className="inline-block text-xs font-bold text-brand-ink/40">PASO {i + 1}</span>
                <p className="font-semibold text-brand-ink mt-1">{s.t}</p>
                <p className="text-sm text-brand-ink/60 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Tips */}
        <section className="mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink mb-8">Tips para una transición efectiva</h2>
          <div className="space-y-6">
            {TIPS.map((tip) => (
              <motion.div key={tip.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="border border-[#E5E5E5] rounded-sm p-6">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: tip.c }}>
                    <tip.icon size={18} className="text-brand-ink" strokeWidth={2.2} />
                  </span>
                  <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">{tip.t}</h3>
                </div>
                <div className="mt-4 text-brand-ink/80 leading-relaxed space-y-2">
                  {tip.body.map((p) => <p key={p}>{p}</p>)}
                  {tip.bullets.length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {tip.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ background: tip.c }} /> {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proceso oficial */}
        <section className="mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink mb-3">Proceso oficial de transición</h2>
          <p className="text-brand-ink/70 mb-8">Sigue estos pasos con la Líder de Clubs de Juventudes Latentes para registrar formalmente tu transición.</p>

          {PROCESO.map((p, i) => (
            <Step key={p.num} num={p.num} icon={p.icon} title={p.title} last={i === PROCESO.length - 1}>
              {p.content}
            </Step>
          ))}

          {/* Plazo destacado */}
          <div className="mt-4 flex items-start gap-3 rounded-sm p-5" style={{ background: "#FFF8E1", border: `1px solid ${COLORS.yellow}` }}>
            <Clock size={22} className="text-brand-ink flex-shrink-0 mt-0.5" />
            <p className="text-brand-ink/85">
              <span className="font-semibold text-brand-ink">Plazo:</span> la nueva mesa y el Plan de Trabajo deben enviarse en un
              plazo máximo de <span className="font-semibold text-brand-ink">30 días</span> a partir de que la nueva líder llena su
              Reporte de Inicio. La reunión de equipo también debe agendarse dentro de esos 30 días.
            </p>
          </div>
        </section>

        {/* Contacto */}
        <div className="mt-14 bg-white border border-[#E5E5E5] rounded-sm p-6 flex items-start gap-3">
          <Mail size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
          <p className="text-brand-ink/80">
            ¿Dudas sobre tu transición? Escribe a{" "}
            <a href={`mailto:${CRISTINA}`} data-testid="cristina-email" className="font-semibold text-brand-magenta hover:underline">{CRISTINA}</a>{" "}
            (Líder de Clubs).
          </p>
        </div>

        {/* Cierre */}
        <div className="mt-8 flex items-center gap-3 rounded-sm p-6 bg-brand-ink">
          <Heart size={22} className="text-brand-magenta flex-shrink-0" fill={COLORS.magenta} />
          <p className="text-white/90">
            No es un adiós, sino un hasta luego. Una vez parte de Juventudes Latentes, siempre será tu casa. ✨
          </p>
        </div>

        {/* CTA */}
        <Link to="/red-de-clubs/directorio-de-clubs" data-testid="directorio-cta" className="mt-8 group flex items-center justify-between gap-4 bg-brand-orange rounded-sm p-8 hover:bg-brand-magenta transition-colors duration-200">
          <div>
            <h3 className="font-heading text-2xl font-semibold tracking-tight text-white">Conoce la Red de Clubs</h3>
            <p className="mt-1 text-white/80">Explora los clubs activos y conecta con más liderazgos.</p>
          </div>
          <ArrowRight size={26} className="text-white flex-shrink-0 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default TransicionLiderazgos;
